"use client"
import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Globe, Users, Target, Shield, Star } from 'lucide-react';
import Link from 'next/link';

// Hook para animação de surgimento
const useIntersectionObserver = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, isVisible] as const;
};

const AnimatedCard = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [ref, isVisible] = useIntersectionObserver();

  return (
    <div
      ref={ref}
      className={`transform transition-all duration-700 ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export const AboutSection = () => {
  return (
    <div className="min-h-screen bg-black py-20 px-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="text-center mb-16">
          <AnimatedCard>
            <div className="inline-flex items-center gap-2 bg-yellow-500 text-black px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star className="w-4 h-4" />
              About Our Organization
            </div>
          </AnimatedCard>
          
          <AnimatedCard delay={200}>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Media Localization <span className="text-yellow-500">Alliance</span>
            </h1>
          </AnimatedCard>
          
          <AnimatedCard delay={400}>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Uniting the world's leading dubbing studios to break language barriers 
              and connect stories with global audiences.
            </p>
          </AnimatedCard>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          
          {/* About MLA Section */}
          <div className="space-y-6">
            <AnimatedCard delay={200}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-yellow-500 rounded-2xl flex items-center justify-center">
                  <Globe className="w-6 h-6 text-black" />
                </div>
                <h2 className="text-3xl font-bold text-white">About MLA</h2>
              </div>
            </AnimatedCard>

            <AnimatedCard delay={300}>
              <div className="bg-gray-900 rounded-3xl p-8 border border-gray-800 shadow-2xl">
                <p className="text-gray-300 leading-relaxed text-lg">
                  The Media Localization Alliance (MLA) is a free, global non-profit organization 
                  with the vision of creating an information hub where Trusted Partner Network (TPN) 
                  members operating TPN certified dubbing studios can access a collaborative network 
                  of nearly 50 studios from around the world, fostering closer connections and mutual 
                  growth opportunities.
                </p>
                
                <div className="mt-6 p-4 bg-white bg-opacity-10 rounded-2xl border border-yellow-500 border-opacity-30">
                  <h3 className="font-semibold text-yellow-400 mb-2">Member Benefits:</h3>
                  <ul className="text-yellow-300 space-y-1 text-sm">
                    <li>• Discounts on studio rentals within the network</li>
                    <li>• Idea exchange and project promotion</li>
                    <li>• Participation in funded in-person meetings</li>
                    <li>• Featured news on MLA website and LinkedIn</li>
                  </ul>
                </div>

                <p className="text-gray-300 leading-relaxed mt-6">
                  MLA serves as a valuable showcase for demonstrating the potential of new technologies 
                  that reduce costs and streamline operations, resulting in faster turnaround times and 
                  profitability.
                </p>
              </div>
            </AnimatedCard>
          </div>

          {/* Objectives Section */}
          <div className="space-y-6">
            <AnimatedCard delay={400}>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-yellow-500 rounded-2xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-black" />
                </div>
                <h2 className="text-3xl font-bold text-white">Our Objectives</h2>
              </div>
            </AnimatedCard>

            <AnimatedCard delay={500}>
              <div className="bg-gray-900 rounded-3xl p-8 border border-gray-800 shadow-2xl">
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Users className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Global Unity</h3>
                      <p className="text-gray-300">
                        Unite leading dubbing studios from around the world to bring stories 
                        to life across languages and cultures.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Shield className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Quality & Compliance</h3>
                      <p className="text-gray-300">
                        Ensure high-quality localization and compliance with content security 
                        best practices that resonate with global audiences.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <Globe className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">Global Connection</h3>
                      <p className="text-gray-300">
                        Break barriers and connect creators with viewers worldwide through 
                        collaborative excellence.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-gray-800 rounded-2xl">
                  <p className="text-gray-300 text-sm italic">
                    "Together, we break barriers and connect creators with viewers worldwide."
                  </p>
                </div>
              </div>
            </AnimatedCard>
          </div>
        </div>

        {/* Key Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <AnimatedCard delay={200}>
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 text-center hover:border-yellow-500 transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">50+ Studios</h3>
              <p className="text-gray-400">Collaborative network of TPN certified studios worldwide</p>
            </div>
          </AnimatedCard>

          <AnimatedCard delay={400}>
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 text-center hover:border-yellow-500 transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">MPA Standards</h3>
              <p className="text-gray-400">Compliant with industry security and quality standards</p>
            </div>
          </AnimatedCard>

          <AnimatedCard delay={600}>
            <div className="bg-gray-900 rounded-2xl p-6 border border-gray-800 text-center hover:border-yellow-500 transition-all duration-300">
              <div className="w-16 h-16 bg-yellow-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-black" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Global Reach</h3>
              <p className="text-gray-400">Connecting stories with audiences across languages and cultures</p>
            </div>
          </AnimatedCard>
        </div>

        {/* CTA Section */}
        <AnimatedCard delay={400}>
          <div className="bg-gray-900 rounded-3xl p-12 text-center border border-gray-800">
            <h2 className="text-3xl md:text-4xl font-bold text-yellow-500 mb-4">
              Ready to Learn More?
            </h2>
            <p className="text-gray-300 text-xl mb-8 max-w-2xl mx-auto">
              Discover how MLA is transforming the media localization industry 
              and creating new opportunities for studios worldwide.
            </p>
            <Link 
              href="/about-us"
              className="inline-flex items-center gap-3 bg-yellow-500 text-black px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-yellow-400 transform hover:scale-105 transition-all duration-300 shadow-lg"
            >
              Explore Full About Page
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </AnimatedCard>

      </div>
    </div>
  );
};